import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Avatar,
    Badge,
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Spinner,
    Switch,
    Text,
    Tooltip,
    makeStyles,
    shorthands,
    tokens
} from '@fluentui/react-components';
import {
    Beaker20Regular,
    Info20Regular,
    Link20Regular,
    LinkDismiss20Regular,
    Person20Regular,
    Star12Filled
} from '@fluentui/react-icons';

// Models
import { Practitioner } from '../../../models/Entities/Practitioners/Practitioner';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';

// Functions
import * as ComponentFuntions from './ContractAssociations.Functions';

// Selectors
import PractitionerSelectorComponent from '../../../commons/component/Inputs/PractitionerSelector/PractitionerSelectorComponent';
import ProtocolSelectorComponent from '../../../commons/component/Inputs/ProtocolSelector/ProtocolSelectorComponent';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalM),
        ...shorthands.padding(tokens.spacingVerticalS, '0')
    },

    // Encabezado
    header: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalS)
    },
    headerIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        flexShrink: 0,
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        backgroundColor: tokens.colorNeutralBackground3,
        color: tokens.colorNeutralForeground2
    },
    headerText: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0
    },
    headerTitle: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: tokens.lineHeightBase400,
        color: tokens.colorNeutralForeground1
    },
    headerSubtitle: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3
    },

    // Tarjeta de asociacion
    card: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalS),
        ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
        backgroundColor: tokens.colorNeutralBackground1
    },
    cardEmpty: {
        ...shorthands.border('1px', 'dashed', tokens.colorNeutralStroke2),
        backgroundColor: tokens.colorNeutralBackground2
    },
    cardDisabled: {
        opacity: 0.55
    },

    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalS)
    },
    cardHeaderIcon: {
        display: 'flex',
        alignItems: 'center',
        color: tokens.colorNeutralForeground3
    },
    cardHeaderLabel: {
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: tokens.colorNeutralForeground3
    },

    // Contenido de la entidad asociada
    entity: {
        display: 'flex',
        alignItems: 'flex-start',
        ...shorthands.gap(tokens.spacingHorizontalM)
    },
    entityBody: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('2px'),
        minWidth: 0,
        flexGrow: 1
    },
    entityTitleRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        ...shorthands.gap(tokens.spacingHorizontalXS)
    },
    entityTitle: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: tokens.lineHeightBase400,
        color: tokens.colorNeutralForeground1,
        wordBreak: 'break-word'
    },
    entitySubtitle: {
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
        color: tokens.colorBrandForeground2
    },
    entityCaption: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        wordBreak: 'break-word'
    },

    emptyText: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground3
    },

    // Acciones
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        ...shorthands.gap(tokens.spacingHorizontalS)
    },
    picker: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalS)
    },
    pickerBusy: {
        opacity: 0.6,
        pointerEvents: 'none'
    },
    busyRow: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalS),
        ...shorthands.padding(tokens.spacingVerticalXS, '0')
    },
    busyLabel: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2
    },

    // Distintivo de contrato principal del protocolo.
    // Se resuelve a mano en vez de con <Badge> para darle un acento propio (marigold) que
    // lo separe del chip de estado, sin el peso visual de un badge "filled" que compite
    // con el titulo del protocolo.
    primaryBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0,
        ...shorthands.gap('3px'),
        ...shorthands.padding('1px', tokens.spacingHorizontalSNudge, '1px', tokens.spacingHorizontalXS),
        ...shorthands.borderRadius(tokens.borderRadiusCircular),
        ...shorthands.border('1px', 'solid', tokens.colorPaletteMarigoldBorder1),
        backgroundColor: tokens.colorPaletteMarigoldBackground1,
        color: tokens.colorPaletteMarigoldForeground2,
        fontSize: tokens.fontSizeBase100,
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: tokens.lineHeightBase200,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        cursor: 'default'
    },
    primaryBadgeIcon: {
        display: 'block',
        flexShrink: 0,
        fontSize: '12px',
        color: tokens.colorPaletteMarigoldForeground1
    },

    // Marca de contrato principal del protocolo
    primaryOption: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('2px'),
        ...shorthands.padding(tokens.spacingVerticalXS, '0')
    },
    primaryHint: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        ...shorthands.margin('0', '0', '0', tokens.spacingHorizontalXXL)
    }
});

const ContractAssociationsComponent = (props: ComponentFuntions.Props) => {
    const styles = useStyles();
    const [t] = useTranslation();

    const [showPractitionerPicker, setShowPractitionerPicker] = useState<boolean>(false);
    const [showProtocolPicker, setShowProtocolPicker] = useState<boolean>(false);
    // Marca la asociación como el contrato principal del protocolo.
    // Solo se puede definir al crear la asociación: la API no expone forma de modificarlo.
    const [isPrimaryContract, setIsPrimaryContract] = useState<boolean>(false);

    const {
        ContractId,
        Contract,
        Practitioner: AssociatedPractitioner,
        Protocol: AssociatedProtocol,
        PractitionerAssociation,
        ProtocolAssociation,
        isAssociatingProtocol,
        isDisassociatingProtocol,
        isAssociatingPractitioner,
        isDisassociatingPractitioner,
        failOnAssociation,
        associationError,
        GetContractById,
        GetAllPractitioners,
        GetAllMedicalProtocols,
        AssociateContractToProtocol,
        AssociateContractToPractitioner,
        DisassociateContractFromProtocol,
        DisassociateContractFromPractitioner
    } = props;

    // El contrato es una adenda: la asociacion se administra desde el contrato original.
    const isAddendum: boolean =
        Contract?.parentContractId !== undefined && Contract?.parentContractId !== null;

    const hasProtocol: boolean = ProtocolAssociation !== undefined;
    const hasPractitioner: boolean = PractitionerAssociation !== undefined;

    const isProtocolBusy: boolean = isAssociatingProtocol || isDisassociatingProtocol;
    const isPractitionerBusy: boolean = isAssociatingPractitioner || isDisassociatingPractitioner;

    useEffect(() => {
        GetContractById(ContractId);
    }, [ContractId, GetContractById]);

    useEffect(() => {
        GetAllPractitioners(1, 1000, '');
        GetAllMedicalProtocols();
    }, [GetAllPractitioners, GetAllMedicalProtocols]);

    // La asociacion quedo registrada en el store: se cierra el selector.
    // Depende de la asociacion del contrato y no de la entidad resuelta, para que el
    // panel se cierre aunque el catalogo de protocolos/profesionales aun no haya cargado.
    useEffect(() => {
        if (ProtocolAssociation) {
            setShowProtocolPicker(false);
            setIsPrimaryContract(false);
        }
    }, [ProtocolAssociation]);

    useEffect(() => {
        if (PractitionerAssociation) setShowPractitionerPicker(false);
    }, [PractitionerAssociation]);

    // events

    const onSelectProtocol = useCallback(
        (selection: Protocol[] | undefined) => {
            const protocol = selection?.[0];
            // Evita disparar una segunda alta mientras la anterior sigue en curso.
            if (!protocol || isProtocolBusy) return;
            AssociateContractToProtocol(ContractId, protocol.id, isPrimaryContract);
        },
        [AssociateContractToProtocol, ContractId, isProtocolBusy, isPrimaryContract]
    );

    const onSelectPractitioner = useCallback(
        (selection: Practitioner[] | undefined) => {
            const practitioner = selection?.[0];
            if (!practitioner || isPractitionerBusy) return;
            AssociateContractToPractitioner(ContractId, practitioner.userId);
        },
        [AssociateContractToPractitioner, ContractId, isPractitionerBusy]
    );

    const onDisassociateProtocol = useCallback(() => {
        if (ProtocolAssociation) DisassociateContractFromProtocol(ProtocolAssociation);
    }, [DisassociateContractFromProtocol, ProtocolAssociation]);

    const onDisassociatePractitioner = useCallback(() => {
        if (PractitionerAssociation) DisassociateContractFromPractitioner(PractitionerAssociation);
    }, [DisassociateContractFromPractitioner, PractitionerAssociation]);

    // shared blocks

    const renderDisassociateButton = (
        onConfirm: () => void,
        busy: boolean,
        entityName: string | undefined
    ) => (
        <Dialog>
            <DialogTrigger disableButtonEnhancement>
                <Button
                    appearance="subtle"
                    size="small"
                    disabled={busy}
                    icon={busy ? <Spinner size="tiny" /> : <LinkDismiss20Regular />}
                >
                    {busy
                        ? t('contractAssociations.status.removing')
                        : t('contractAssociations.protocolAssociated.disassociate')}
                </Button>
            </DialogTrigger>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>{t('contractAssociations.confirm.title')}</DialogTitle>
                    <DialogContent>
                        <Text>
                            {t('contractAssociations.confirm.message', {
                                name: entityName ?? ''
                            })}
                        </Text>
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="secondary">
                                {t('contractAssociations.confirm.cancel')}
                            </Button>
                        </DialogTrigger>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="primary" onClick={onConfirm}>
                                {t('contractAssociations.confirm.accept')}
                            </Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );

    const renderBusyRow = (label: string) => (
        <div className={styles.busyRow}>
            <Spinner size="tiny" />
            <span className={styles.busyLabel}>{label}</span>
        </div>
    );

    // Protocol card

    const protocolCard = (
        <div
            className={[
                styles.card,
                !hasProtocol ? styles.cardEmpty : '',
                !hasProtocol && hasPractitioner ? styles.cardDisabled : ''
            ]
                .filter(Boolean)
                .join(' ')}
        >
            <div className={styles.cardHeader}>
                <span className={styles.cardHeaderIcon}>
                    <Beaker20Regular />
                </span>
                <span className={styles.cardHeaderLabel}>
                    {t('contractAssociations.protocol.label')}
                </span>
            </div>

            {hasProtocol ? (
                <>
                    <div className={styles.entity}>
                        <div className={styles.entityBody}>
                            <div className={styles.entityTitleRow}>
                                <span className={styles.entityTitle}>
                                    {AssociatedProtocol?.title ??
                                        t('contractAssociations.status.loadingDetail')}
                                </span>
                                {AssociatedProtocol?.status?.title && (
                                    <Badge appearance="tint" color="informative" size="small">
                                        {AssociatedProtocol.status.title}
                                    </Badge>
                                )}
                                {ProtocolAssociation?.isPrimary && (
                                    <Tooltip
                                        relationship="description"
                                        content={t('contractAssociations.primary.tooltip')}
                                    >
                                        <span className={styles.primaryBadge}>
                                            <Star12Filled
                                                className={styles.primaryBadgeIcon}
                                            />
                                            {t('contractAssociations.primary.badge')}
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                            {AssociatedProtocol?.specialty?.description && (
                                <span className={styles.entitySubtitle}>
                                    {AssociatedProtocol.specialty.description}
                                </span>
                            )}
                            {AssociatedProtocol?.fullTitle && (
                                <span className={styles.entityCaption}>
                                    {AssociatedProtocol.fullTitle}
                                </span>
                            )}
                        </div>
                    </div>
                    {!isAddendum && (
                        <div className={styles.actions}>
                            {renderDisassociateButton(
                                onDisassociateProtocol,
                                isDisassociatingProtocol,
                                AssociatedProtocol?.title
                            )}
                        </div>
                    )}
                </>
            ) : showProtocolPicker ? (
                <div className={styles.picker}>
                    <Text className={styles.emptyText}>
                        {t('contractAssociations.protocolAssociation.selectProtocol')}
                    </Text>
                    <div className={styles.primaryOption}>
                        <Switch
                            checked={isPrimaryContract}
                            disabled={isAssociatingProtocol}
                            label={t('contractAssociations.primary.switchLabel')}
                            onChange={(_, data) => setIsPrimaryContract(data.checked)}
                        />
                        <span className={styles.primaryHint}>
                            {t('contractAssociations.primary.switchHint')}
                        </span>
                    </div>
                    <div className={isAssociatingProtocol ? styles.pickerBusy : undefined}>
                        <ProtocolSelectorComponent
                            selectionLimit={1}
                            onChangeSelection={onSelectProtocol}
                        />
                    </div>
                    {isAssociatingProtocol &&
                        renderBusyRow(t('contractAssociations.status.registering'))}
                    <div className={styles.actions}>
                        <Button
                            appearance="subtle"
                            size="small"
                            disabled={isAssociatingProtocol}
                            onClick={() => {
                                setShowProtocolPicker(false);
                                setIsPrimaryContract(false);
                            }}
                        >
                            {t('contractAssociations.confirm.cancel')}
                        </Button>
                    </div>
                </div>
            ) : (
                <>
                    <Text className={styles.emptyText}>
                        {t('contractAssociations.protocol.empty')}
                    </Text>
                    {!isAddendum && !hasPractitioner && (
                        <div className={styles.actions}>
                            <Button
                                appearance="secondary"
                                size="small"
                                icon={<Link20Regular />}
                                onClick={() => setShowProtocolPicker(true)}
                            >
                                {t('contractAssociations.noAssociations.associateProtocol')}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    // Practitioner card

    const practitionerCard = (
        <div
            className={[
                styles.card,
                !hasPractitioner ? styles.cardEmpty : '',
                !hasPractitioner && hasProtocol ? styles.cardDisabled : ''
            ]
                .filter(Boolean)
                .join(' ')}
        >
            <div className={styles.cardHeader}>
                <span className={styles.cardHeaderIcon}>
                    <Person20Regular />
                </span>
                <span className={styles.cardHeaderLabel}>
                    {t('contractAssociations.practitionerAssociated.associatedMedical')}
                </span>
            </div>

            {hasPractitioner ? (
                <>
                    <div className={styles.entity}>
                        <Avatar
                            name={AssociatedPractitioner?.fullName}
                            image={
                                AssociatedPractitioner?.photo
                                    ? { src: AssociatedPractitioner.photo }
                                    : undefined
                            }
                            size={40}
                            color="colorful"
                        />
                        <div className={styles.entityBody}>
                            <span className={styles.entityTitle}>
                                {AssociatedPractitioner?.fullName ??
                                    t('contractAssociations.status.loadingDetail')}
                            </span>
                            {AssociatedPractitioner?.speciality && (
                                <span className={styles.entitySubtitle}>
                                    {AssociatedPractitioner.speciality}
                                </span>
                            )}
                            {AssociatedPractitioner?.registrationNumber !== undefined && (
                                <span className={styles.entityCaption}>
                                    {t('contractAssociations.practitioner.registrationNumber', {
                                        number: AssociatedPractitioner.registrationNumber
                                    })}
                                </span>
                            )}
                        </div>
                    </div>
                    {!isAddendum && (
                        <div className={styles.actions}>
                            {renderDisassociateButton(
                                onDisassociatePractitioner,
                                isDisassociatingPractitioner,
                                AssociatedPractitioner?.fullName
                            )}
                        </div>
                    )}
                </>
            ) : showPractitionerPicker ? (
                <div className={styles.picker}>
                    <Text className={styles.emptyText}>
                        {t('contractAssociations.practitionerAssociation.selectPractitioners')}
                    </Text>
                    <div className={isAssociatingPractitioner ? styles.pickerBusy : undefined}>
                        <PractitionerSelectorComponent
                            MultiselectEnable={false}
                            SelectedPractitioners={[]}
                            onChangeSelection={onSelectPractitioner}
                        />
                    </div>
                    {isAssociatingPractitioner &&
                        renderBusyRow(t('contractAssociations.status.registering'))}
                    <div className={styles.actions}>
                        <Button
                            appearance="subtle"
                            size="small"
                            disabled={isAssociatingPractitioner}
                            onClick={() => setShowPractitionerPicker(false)}
                        >
                            {t('contractAssociations.confirm.cancel')}
                        </Button>
                    </div>
                </div>
            ) : (
                <>
                    <Text className={styles.emptyText}>
                        {t('contractAssociations.practitioner.empty')}
                    </Text>
                    {!isAddendum && !hasProtocol && (
                        <div className={styles.actions}>
                            <Button
                                appearance="secondary"
                                size="small"
                                icon={<Link20Regular />}
                                onClick={() => setShowPractitionerPicker(true)}
                            >
                                {t(
                                    'contractAssociations.noAssociations.associateMedicalProfessional'
                                )}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <span className={styles.headerIcon}>
                    <Link20Regular />
                </span>
                <div className={styles.headerText}>
                    <span className={styles.headerTitle}>{t('contractAssociations.title')}</span>
                    <span className={styles.headerSubtitle}>
                        {t('contractAssociations.subtitle')}
                    </span>
                </div>
            </div>

            {isAddendum && (
                <MessageBar intent="info" icon={<Info20Regular />}>
                    <MessageBarBody>
                        <MessageBarTitle>
                            {t('contractAssociations.addendum.title')}
                        </MessageBarTitle>
                        {t('contractAssociations.addendum.message')}
                    </MessageBarBody>
                </MessageBar>
            )}

            {failOnAssociation && (
                <MessageBar intent="error">
                    <MessageBarBody>
                        <MessageBarTitle>{t('contractAssociations.error.title')}</MessageBarTitle>
                        {associationError?.ErrorMessage ?? t('contractAssociations.error.generic')}
                    </MessageBarBody>
                </MessageBar>
            )}

            {protocolCard}
            {practitionerCard}
        </div>
    );
};

export default connect(
    ComponentFuntions.mapStateToProps,
    ComponentFuntions.mapDispatchToProps
)(ContractAssociationsComponent as any);
